import { useState, useEffect } from 'react';
import { api, getCurrentLocale } from '../services/api';

export interface PriceCardItem {
  id: number;
  title?: string;
  description?: string;
  features?: string[];
  price?: string;
  highlighted?: boolean;
}

export interface CombinedPriceListData {
  title?: string;
  description?: string; // formerly subtitle in UI
  disclaimer?: string; // richtext (HTML)
  priceCards: PriceCardItem[];
}

export const usePriceList = () => {
  const [data, setData] = useState<CombinedPriceListData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const locale = typeof window !== 'undefined' ? getCurrentLocale() : 'nl';

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [priceListRes, priceCardsRes] = await Promise.all([
          api.get('/price-list?populate=*'),
          api.get('/price-cards?populate=features&sort=order:asc'),
        ]);

        // Handle Strapi Cloud flattened response OR classic { attributes } shape
        const plRaw = priceListRes?.data?.data || {};
        const pl = (plRaw && (plRaw as any).attributes) ? (plRaw as any).attributes : plRaw;

        const cardsRaw: any[] = Array.isArray(priceCardsRes?.data?.data)
          ? priceCardsRes.data.data
          : [];

        const priceCards: PriceCardItem[] = cardsRaw.map((item) => {
          const base = (item && (item as any).attributes) ? (item as any).attributes : item || {};
          // Normalize features to string[] supporting both legacy JSON and new repeatable component
          let features: string[] = [];
          if (Array.isArray(base?.features)) {
            // Could be legacy string[] or new component array [{text}]
            features = (base.features as any[]).map((f: any) => (typeof f === 'string' ? f : f?.text)).filter(Boolean);
          }
          return {
            id: item.id,
            title: base?.title,
            description: base?.description,
            features,
            price: base?.price,
            highlighted: Boolean(base?.isPopular || base?.highlighted),
          };
        });

        setData({
          title: pl?.title,
          description: pl?.description,
          disclaimer: pl?.disclaimer,
          priceCards,
        });
      } catch (err: any) {
        if (err?.response?.status !== 404) {
          console.log('Error fetching price list:', err);
        }
        setError(err?.message || 'Failed to fetch price list');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [typeof window !== 'undefined' ? getCurrentLocale() : 'nl']);

  return { data, loading, error };
};